import { useEffect, useRef, useReducer, useState } from 'react';
import styles from './DropdownSelect.module.css';
import SelectItem from './SelectItem';

export type Option = {
    checked: boolean;
    label: string;
};

const DropdownSelect: React.FC<{
    isSearchable?: boolean;
    isMulti?: boolean;
    name?: string;
    options: { [key: string]: boolean | Option };
    labelled?: boolean;
    onChange: Function;
}> = ({
    isSearchable = false,
    isMulti,
    name,
    options,
    onChange,
    labelled = false,
}) => {
    const [data, setData] = useState<{ [key: string]: boolean | Option }>();
    const [open, setOpen] = useState(false);

    const [searchedTerm, setSearchedTerm] = useState('');

    const updateData = (value: string) => {
        if (
            (!labelled && data[value]) ||
            (labelled && (data[value] as Option).checked)
        ) {
            let tempObj = { ...data };
            if (labelled) {
                tempObj[value] = {
                    checked: false,
                    label: (tempObj[value] as Option).label,
                };
            } else {
                tempObj[value] = false;
            }
            setData(tempObj);
        } else if (isMulti) {
            let tempObj = { ...data };
            if (labelled) {
                tempObj[value] = {
                    checked: true,
                    label: (tempObj[value] as Option).label,
                };
            } else {
                tempObj[value] = true;
            }
            setData(tempObj);
        } else {
            let tempObj: Record<string, Option | boolean> = Object.keys(
                options
            ).reduce((acc, item) => {
                if (labelled) {
                    acc[item] = {
                        checked: false,
                        label: (options[item] as Option).label,
                    };
                } else {
                    acc[item] = false;
                }
                return acc;
            }, {} as Record<string, Option | boolean>);
            if (labelled) {
                tempObj[value] = {
                    checked: true,
                    label: (tempObj[value] as Option).label,
                };
            } else {
                tempObj[value] = true;
            }
            setData(tempObj);
        }
    };
    // setting the values of types, manufactures, fieldStrength / so they are only called once except if there is a change
    useEffect(() => {
        setData(options);
    }, [options]);

    const toggle = () => {
        if (open) {
            setOpen(false);
            onChange(data);
        } else {
            setOpen(true);
        }
    };

    const optionsRef = useRef(null);
    const triggerRef = useRef(null);

    function handleClickOutside(event: any) {
        if (
            optionsRef.current &&
            !optionsRef.current.contains(event.target) &&
            triggerRef.current &&
            !triggerRef.current.contains(event.target)
        ) {
            toggle();
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, data]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.select}>
                {/* Filter-pannel */}
                <div ref={triggerRef} className={styles.inputWrapper}>
                    {!isSearchable && (
                        <div onClick={toggle} className={styles.trigger}>
                            <span>{name}</span>
                            <div
                                className={`${styles.arrow} ${
                                    open ? styles.open : ''
                                }`}
                            ></div>
                        </div>
                    )}
                    {isSearchable && (
                        <input
                            className={styles.input}
                            type="text"
                            placeholder="Search, select multiple types"
                            onFocus={() => setOpen(true)}
                            onChange={(e) => setSearchedTerm(e.target.value)}
                        />
                    )}
                </div>
                {open && (
                    <div ref={optionsRef} className={styles.options}>
                        {' '}
                        {Object.entries(data).map((item) =>
                            item[0]
                                .toLowerCase()
                                .includes(searchedTerm.toLowerCase()) ? (
                                <SelectItem
                                    key={item[0]}
                                    defaultChecked={
                                        labelled
                                            ? (item[1] as Option).checked
                                            : (item[1] as boolean)
                                    }
                                    className={styles.item}
                                    type={isMulti ? 'checkbox' : 'radio'}
                                    value={item[0]}
                                    name={name}
                                    label={
                                        labelled
                                            ? (item[1] as Option).label
                                            : undefined
                                    }
                                    onChange={updateData}
                                />
                            ) : (
                                ''
                            )
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DropdownSelect;
